import React, { useState, useRef, useEffect } from "react";
import { Constants } from "../../../constants/appConstants";
import { Ordersnew } from "../../../models/csos/ordersNew";
import { OrdersUpdate } from "../../../models/csos/ordersUpdate";
import { csosApiService } from "../../../services/api/csosApiService";
import { userService } from "../../../services/userService";
import ModalContentCenter from "../../modalContentCenter/ModalContentCenter";
import "./UTNUpdate.css";

interface UTNUpdateProps {
  order: any;
  utnPrefix: string;
  utnSuffix: string;
  onClose: () => void;
}

const UTNUpdate: React.FC<UTNUpdateProps> = ({ order, utnPrefix, utnSuffix: initialSuffix, onClose }) => {
  const [utnSuffix, setUtnSuffix] = useState(initialSuffix);
  const [validation, setValidation] = useState(true);
  const [message, setMessage] = useState("");
  const [saveUTNButton, setSaveUTNButton] = useState(true);
  const [showDuplicateUTN, setShowDuplicateUTN] = useState(false);
  const [showErrorUTNDTK, setShowErrorUTNDTK] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (cancelRef.current) cancelRef.current.focus();
  }, []);

  const updateUpperCaseUTN = (value: string) => {
    const upperValue = value.toUpperCase();
    setUtnSuffix(upperValue);
    const regex = /^[a-zA-Z0-9]*$/;

    if (!regex.test(upperValue) || upperValue.length < 6) {
      setSaveUTNButton(true);
      setMessage(Constants.UTN_ERROR);
      setValidation(false);
    } else if (order.uniqueTrackingNum && upperValue === order.uniqueTrackingNum.slice(3)) {
      setSaveUTNButton(true);
      setValidation(true);
    } else {
      setValidation(true);
      setSaveUTNButton(false);
    }
  };

  const validateUTN = (value: string) => {
    updateUpperCaseUTN(value);
  };

  const resetUtn = () => {
    setShowErrorUTNDTK(false);
    setUtnSuffix(order.uniqueTrackingNum ? order.uniqueTrackingNum.slice(3) : initialSuffix);
    setValidation(true);
    setSaveUTNButton(true);
    onClose();
  };

  const updateCsosOrders = () => {
    if (utnSuffix.length === 6 && order.uniqueTrackingNum !== (utnPrefix + utnSuffix)) {
      const ordersUpdate = new OrdersUpdate();
      ordersUpdate.operationName = Constants.Operation_Name_UPDATE_UTN;
      ordersUpdate.orders = [{
        customerNumber: order.customerNum,
        distributionCenter: order.dcNum,
        orderCreateSTP: order.submitted,
        orderId: order.orderId,
        uniqueTrackingNumber: utnPrefix + utnSuffix,
        uniqueTrackingNumberOld: order.uniqueTrackingNum,
      }];
      ordersUpdate.statusCode = order.status;
      csosApiService.updateCsosOrders(ordersUpdate).then((response) => {
        if (response.body && response.body.hostCode === "0000" && response.body.hostMessage === "SUCCESS") {
          // refresh order detail to get new UTN
          const account = order.dcNum + "-" + order.customerNum;
          csosApiService.getPendingOrder(order.orderId, account).then((res) => {
            if (res.body && res.body.orders[0]) {
              order.uniqueTrackingNum = res.body.orders[0].uniqueTrackingNum;
            }
          });
          onClose();
        } else if (response.body && response.body.hostCode === "3001") {
          setShowDuplicateUTN(true);
        } else if (response.body && response.body.hostMessage === "DTK service is unavailable") {
          onClose();
          setShowErrorUTNDTK(true);
        } else {
          setShowErrorUTNDTK(true);
        }
      }).catch(() => {
        setShowErrorUTNDTK(true);
        resetUtn();
      });
    }
  };

  return (
    <>
      <ModalContentCenter visible={true}>
        <div className="modal-fade updateUTNModal" role="dialog">
          <div className="utn-modal-dialog">
            <div className="utn-modal-content">
              <div className="utn-modal-header">
                <button type="button" className="close" onClick={resetUtn}>&times;</button>
                <h4 className="utn-modal-title">Update UTN</h4>
              </div>
              <div className="utn-modal-body">
                <div className="form-group">
                  <label htmlFor="UTN" className="UTNlabel col-md-6">Update UTN To <span className="utnprefix">{utnPrefix}</span></label>
                  <div>
                    <input
                      type="text"
                      className="form-control UTNupdate"
                      id="UTN"
                      autoComplete="off"
                      onPaste={(e) => e.preventDefault()}
                      name="UTNupdate"
                      value={utnSuffix}
                      onChange={(e) => validateUTN(e.target.value)}
                      maxLength={6}
                      onInput={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.value = target.value.toUpperCase();
                      }}
                    />
                  </div>
                </div>
                {!validation && <div><span className="error">{message}</span></div>}
                <div>
                  <p className="italic">
                    Clicking save will update the Unique Tracking Number (UTN). The UTN must be unique to the DEA License and contain 6 letters or numbers.
                  </p>
                </div>
              </div>
              <div className="utn-modal-footer">
                <button type="button" className="btn btn-default cancelbtn" ref={cancelRef} onClick={resetUtn}>Cancel</button>
                <button type="button" className="btn btn-default savebtn" onClick={updateCsosOrders} disabled={saveUTNButton}>Save</button>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>

      <ModalContentCenter visible={showDuplicateUTN}>
        <div className="modal-fade" role="dialog">
          <div className="utn-modal-dialog">
            <div className="utn-modal-content">
              <div className="utn-modal-header">
                <h4 className="utn-modal-title">Alert</h4>
              </div>
              <div className="utn-modal-body">
                <p>The provided UTN has already been used by this account.</p>
              </div>
              <div className="utn-modal-footer">
                <button type="button" className="btn btn-default utn-signorder" onClick={() => setShowDuplicateUTN(false)}>OK</button>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>

      <ModalContentCenter visible={showErrorUTNDTK}>
        <div className="modal-fade DTKfailed" role="dialog">
          <div className="utn-modal-dialog">
            <div className="utn-modal-content">
              <div className="utn-modal-header">
                <button type="button" className="btn btn-default close" onClick={resetUtn}>&times;</button>
                <h4 className="utn-modal-title">Alert</h4>
              </div>
              <div className="utn-modal-body">
                <p className="center">Please reconfirm details and resubmit order.</p>
              </div>
              <div className="utn-modal-footer">
                <button type="button" className="btn btn-default overrideDTK" onClick={() => setShowErrorUTNDTK(false)}>OK</button>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>
    </>
  );
};

export default UTNUpdate;
